import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  createStyles,
  Grid,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  withStyles,
  WithStyles
} from "@material-ui/core";
import fromPairs from "lodash/fromPairs";
import React, { useEffect, useState } from "react";

import {
  canAdd,
  Integration,
  IntegrationStatus,
  INTEGRATIONS
} from "./registry";

const styles = () =>
  createStyles({
    headerRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 8
    },
    title: {
      fontWeight: 600
    }
  });

interface Props extends WithStyles<typeof styles> {
  factoringId: string;
  isBroker: boolean;
  allowed: (permission: string) => boolean;
  openSnackbar: (variant: string, message: string) => void;
}

interface Load {
  status?: IntegrationStatus;
  failed?: boolean;
}

const ConnectedIntegrations = ({
  classes,
  factoringId,
  isBroker,
  allowed,
  openSnackbar
}: Props): JSX.Element | null => {
  const integrations = INTEGRATIONS.filter(
    integration =>
      allowed(integration.permission) && (isBroker || !integration.brokersOnly)
  );
  const keys = integrations.map(integration => integration.key).join(",");
  const [loads, setLoads] = useState<Record<string, Load> | null>(null);
  const [addedNow, setAddedNow] = useState<string[]>([]);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    let isActive = true;
    setLoads(null);
    setAddedNow([]);
    Promise.all(
      integrations.map(
        async (integration): Promise<[string, Load]> => {
          try {
            return [
              integration.key,
              { status: await integration.loadStatus(factoringId) }
            ];
          } catch (error) {
            return [integration.key, { failed: true }];
          }
        }
      )
    ).then(entries => {
      if (isActive) setLoads(fromPairs(entries));
    });
    return (): void => {
      isActive = false;
    };
  }, [factoringId, keys]);

  if (!integrations.length) return null;

  const loadOf = (integration: Integration): Load =>
    (loads && loads[integration.key]) || {};
  const isShown = (integration: Integration): boolean => {
    const { status, failed } = loadOf(integration);
    return Boolean(
      failed ||
        (status && status.added) ||
        addedNow.includes(integration.key)
    );
  };
  const shown = integrations.filter(isShown);
  const addable = integrations.filter(
    integration => !isShown(integration) && canAdd(loadOf(integration).status)
  );
  const anyAvailable = integrations.some(integration => {
    const { status } = loadOf(integration);
    return Boolean(status && status.available);
  });

  const add = (key: string): void => {
    setMenuAnchor(null);
    setAddedNow(current => [...current, key]);
  };

  return (
    <>
      <Grid item style={{ width: "100%" }}>
        <Card>
          <CardContent>
            <div className={classes.headerRow}>
              <Typography variant="h6" className={classes.title}>
                Connected Integrations
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                disabled={!addable.length}
                onClick={(event): void => setMenuAnchor(event.currentTarget)}
              >
                Add Integration
              </Button>
            </div>
            {loads ? (
              <>
                {!shown.length && (
                  <Typography>No integrations are connected yet.</Typography>
                )}
                {!addable.length && (
                  <Typography variant="caption">
                    {anyAvailable
                      ? "Every available integration is added."
                      : "No integrations are switched on for this server."}
                  </Typography>
                )}
              </>
            ) : (
              <CircularProgress size={24} />
            )}
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={(): void => setMenuAnchor(null)}
            >
              {addable.map(integration => (
                <MenuItem
                  key={integration.key}
                  onClick={(): void => add(integration.key)}
                >
                  <ListItemText
                    primary={integration.name}
                    secondary={integration.description}
                  />
                </MenuItem>
              ))}
            </Menu>
          </CardContent>
        </Card>
      </Grid>
      {shown.map(integration => (
        <Grid item key={integration.key} style={{ width: "100%" }}>
          <integration.Card
            factoringId={factoringId}
            openSnackbar={openSnackbar}
          />
        </Grid>
      ))}
    </>
  );
};

export default withStyles(styles)(ConnectedIntegrations);
